import React, { useState, useEffect } from 'react';
import { Popover, Box, Typography, IconButton, Button, useMediaQuery, useTheme, Divider, InputAdornment } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { DateRangePickerInput, DateRangePickerWrapper } from './styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

dayjs.extend(isBetween);

interface CustomDateRangePickerProps {
    value: [Dayjs | null, Dayjs | null];
    onChange: (newValue: [Dayjs | null, Dayjs | null]) => void;
    mode?: 'range' | 'week';
    maxDays?: number;
}

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({ value, onChange, mode = 'range', maxDays }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(value);
    const [displayMonth, setDisplayMonth] = useState(dayjs().startOf('month'));
    const [hoveredDate, setHoveredDate] = useState<Dayjs | null>(null);
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        setDateRange(value);
        if (value[0]) {
            setDisplayMonth(value[0].startOf('month'));
        }
    }, [value]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'custom-date-range-picker' : undefined;

    const formatRange = (range: [Dayjs | null, Dayjs | null]) => {
        const [start, end] = range;
        if (!start && !end) return '';
        if (start && !end) return `${start.format('DD/MM/YYYY')} - ...`;
        if (start && end) return `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
        return '';
    };

    const handlePrevMonth = () => {
        setDisplayMonth(displayMonth.subtract(1, 'month'));
    };

    const handleNextMonth = () => {
        setDisplayMonth(displayMonth.add(1, 'month'));
    };

     const handleDateClick = (day: Dayjs) => {
        if (mode === 'week') {
            const startOfWeek = day.startOf('week');
            const endOfWeek = day.endOf('week');
            const newRange: [Dayjs | null, Dayjs | null] = [startOfWeek, endOfWeek];
            setDateRange(newRange);
            onChange(newRange);
            setTimeout(handleClose, 200);
            return;
        }

        const [start, end] = dateRange;
        if (!start || (start && end)) {
            setDateRange([day, null]);
        } else if (day.isBefore(start)) {
            setDateRange([day, null]);
        } else if (maxDays && day.diff(start, 'day') >= maxDays) {
            setDateRange([day, null]);
        } else {
            const newRange: [Dayjs | null, Dayjs | null] = [start, day];
            setDateRange(newRange);
            onChange(newRange);
            setTimeout(handleClose, 200);
        }
    };
    
    const Calendar = ({ month, showPrev, showNext }: { month: Dayjs; showPrev?: boolean; showNext?: boolean }) => {
        const startOfMonth = month.startOf('month');
        const endOfMonth = month.endOf('month');
        const daysInMonth = endOfMonth.date();
        const startDayOfWeek = startOfMonth.day(); // 0 for Sunday, 6 for Saturday

        const renderDays = () => {
            const days = [];
            //- Pad start of month
            for (let i = 0; i < startDayOfWeek; i++) {
                days.push(<Box key={`pad-start-${i}`} sx={{ flexBasis: 'calc(100% / 7)' }} />);
            }

            //- Render days
            for (let i = 1; i <= daysInMonth; i++) {
                const date = startOfMonth.date(i);
                const isStart = dateRange[0] && date.isSame(dateRange[0], 'day');
                const isEnd = dateRange[1] && date.isSame(dateRange[1], 'day');
                const inRange = dateRange[0] && dateRange[1] && date.isBetween(dateRange[0], dateRange[1], 'day', '[]');

                let isHoveredRange = false;
                if (mode === 'week' && hoveredDate) {
                    const hoveredStart = hoveredDate.startOf('week');
                    const hoveredEnd = hoveredDate.endOf('week');
                    isHoveredRange = date.isBetween(hoveredStart, hoveredEnd, 'day', '[]');
                }

                let isDisabled = false;
                if (maxDays && dateRange[0] && !dateRange[1]) {
                    const start = dateRange[0];
                    const diff = date.diff(start, 'day');
                    isDisabled = date.isBefore(start) || diff >= maxDays;
                }

                const showLeftRound = isStart || (isHoveredRange && date.day() === 0);
                const showRightRound = isEnd || (isHoveredRange && date.day() === 6);

                days.push(
                    <Box 
                        key={i} 
                        sx={{ 
                            flexBasis: 'calc(100% / 7)', 
                            display: 'flex', 
                            justifyContent: 'center', 
                            paddingY: '2px', 
                            position: 'relative',
                            backgroundColor: inRange ? '#E3F2FD' : (isHoveredRange ? '#E3F2FD80' : 'transparent'),
                            borderTopLeftRadius: showLeftRound ? '18px' : 0,
                            borderBottomLeftRadius: showLeftRound ? '18px' : 0,
                            borderTopRightRadius: showRightRound ? '18px' : 0,
                            borderBottomRightRadius: showRightRound ? '18px' : 0,
                        }}
                    >
                        <Button
                            variant="text"
                            disabled={isDisabled}
                            sx={{
                                borderRadius: '50%',
                                minWidth: 36,
                                width: 36,
                                height: 36,
                                fontSize: '0.85rem',
                                fontWeight: (isStart || isEnd) ? 700 : 500,
                                color: isDisabled ? '#ccc' : '#333',
                                border: (isStart || isEnd) ? '1px solid #1976D2' : '1px solid transparent',
                                backgroundColor: (isStart || isEnd) ? '#E3F2FD !important' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isDisabled ? 'transparent' : '#f0f0f0',
                                }
                            }}
                            onClick={() => !isDisabled && handleDateClick(date)}
                            onMouseEnter={() => !isDisabled && setHoveredDate(date)}
                            onMouseLeave={() => setHoveredDate(null)}
                        >
                            {i}
                        </Button>
                    </Box>
                );
            }
            return days;
        };
        
        const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

        return (
            <Box sx={{ overflow: 'hidden', width: isMobile ? '100%' : 308 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <IconButton 
                        onClick={handlePrevMonth} 
                        size="small"
                        sx={{ visibility: (showPrev || isMobile) ? 'visible' : 'hidden' }}
                    >
                        <ChevronLeft />
                    </IconButton>
                    
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontSize: '0.9rem', 
                            fontWeight: 600, 
                            color: '#333' 
                        }}
                    >
                        {month.format('MMMM YYYY')}
                    </Typography>

                    <IconButton 
                        onClick={handleNextMonth} 
                        size="small"
                        sx={{ visibility: (showNext || isMobile) ? 'visible' : 'hidden' }}
                    >
                        <ChevronRight />
                    </IconButton>
                </Box>
                <Box display="flex" flexWrap="nowrap" mb={1}>
                    {weekDays.map(day => (
                        <Box key={day} sx={{ flexBasis: 'calc(100% / 7)', textAlign: 'center' }}>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: '#999', 
                                    fontWeight: 700, 
                                    fontSize: '0.75rem' 
                                }}
                            >
                                {day}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Box display="flex" flexWrap="wrap" minHeight={264} justifyContent="flex-start">
                    {renderDays()}
                </Box>
            </Box>
        );
    };

    return (
        <DateRangePickerWrapper>
            <DateRangePickerInput
                id={id}
                fullWidth
                placeholder="MM/DD/YYYY - MM/DD/YYYY"
                onClick={handleClick}
                value={formatRange(dateRange)}
                size='small'
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={handleClick} edge="end" size="small">
                                <CalendarMonthIcon sx={{ color: '#5E8E7E', fontSize: 20 }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                        border: '1px solid #f0f0f0',
                        mt: 1
                    }
                }}
            >
                <Box p={4} sx={{ minWidth: isMobile ? 326 : 700, maxWidth: isMobile ? 326 : 700 }}>
                    <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2} justifyContent="center">
                        <Box flex={1}>
                            <Calendar month={displayMonth} showPrev />
                        </Box>
                        {!isMobile && (
                            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: '#f0f0f0' }} />
                        )}
                        {!isMobile && (
                            <Box flex={1}>
                                <Calendar month={displayMonth.add(1, 'month')} showNext />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Popover>
        </DateRangePickerWrapper>
    );
};

export default CustomDateRangePicker;
